// src/components/FilterSelect.tsx
import React, { ReactNode, useState } from 'react';
import {
  FormControl,
  MenuItem,
  SelectChangeEvent,
  ListSubheader,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Select from '@mui/material/Select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// StyledSelect customizes the closed‐state appearance of MUI’s Select
const StyledSelect = styled(Select)({
  boxSizing: 'border-box',
  padding: '16px',
  gap: '4px',
  height: 56,
  width: 'fit-content',
  background: '#F5F5F5',
  border: '1px solid #464646',
  borderRadius: 16,
  letterSpacing: 0,
  '& fieldset': { border: 'none' },
  '& .MuiSelect-select': {
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'unset',
  },
  // keep the arrow icon flush right and animate its rotation
  '& .MuiSelect-icon': {
    right: 16,
    transition: 'transform 200ms ease',
  },
});

interface Option {
  value: string;
  label: string;
}

interface FilterSelectProps {
  value: string;
  onChange: (event: SelectChangeEvent<any>, child?: ReactNode) => void;
  options: Option[];
}

export default function FilterSelect({
  value,
  onChange,
  options,
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);

  return (
    <FormControl>
      <StyledSelect
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        // only show the label when closed (no icon or prefix)
        renderValue={(selected) => {
          const opt = options.find((o) => o.value === selected);
          return (
            <>
              {opt?.label ? (
                <>
                  {opt.label.split(':')[0]}
                  {opt.label.includes(':') && (
                    <>
                      : <strong>{opt.label.split(':')[1].trim()}</strong>
                    </>
                  )}
                </>
              ) : (
                ''
              )}
            </>
          );
        }}
        value={value}
        onChange={onChange}
        // swap up/down arrow based on open state
        IconComponent={open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            sx: {
              borderRadius: '16px',
              mt: 1,
              minWidth: 240,
              p: 0,
              overflow: 'hidden',
            },
          },
          MenuListProps: { sx: { p: 0 } },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        }}
      >
        {/* Dropdown header */}
        <ListSubheader
          component='div'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            bgcolor: 'white',
            borderBottom: '1px solid #EEE',
          }}
        >
          <Typography color='black' variant='subtitle1'>
            Data Type:
          </Typography>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </ListSubheader>

        {/* Menu options */}
        {options.map((opt) => {
          const isSelected = value === opt.value;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 56,

                // hover
                '&:hover': {
                  backgroundColor: '#FFE8EF',
                },

                // selected (mouse) • selected-while-hovered • selected-while-focused
                '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible':
                  {
                    backgroundColor: '#FFE8EF',
                  },
              }}
            >
              {isSelected ? (
                <CheckCircleIcon color='error' sx={{ mr: 1 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ mr: 1 }} />
              )}
              {opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}
            </MenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
}
